import React, { useEffect, useState, useRef } from "react";
import {
  getAddress,
  EditAddress,
  AddAddress,
  DeleteAddress,
  SetDefaultAddress,
  stateList,
  getGst
} from "../services/Cart";
import "../styles/Address.css";
import { showSnackbar } from "./snackbar";
import LoadingSpinner from "./LoadingSpinner";

const Address = () => {
  const [address, setAddress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [statecode, setstateCode] = useState('')
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);
  const gstFetchIdRef = useRef(0);
  const lastFetchedGstRef = useRef("");

  // New address form state
  const [newAddress, setNewAddress] = useState({
    name: "",
    firmname: "",
    phone: "",
    altphone: "",
    email: "",
    flathouse: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    type: "",
    gstno: "",
    useSameAddressForBilling: true,
  });

  const userId = localStorage.getItem("userId");

  // Validation function for address form
  const validateAddressForm = (addressData) => {
    const errors = {};

    // Name validation
    if (!addressData.name?.trim()) {
      errors.name = "Full name is required";
    } else if (addressData.name.trim().length < 3) {
      errors.name = "Name must be at least 3 characters";
    }

    // Phone validation
    if (!addressData.phone?.trim()) {
      errors.phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(addressData.phone)) {
      errors.phone = "Please enter a valid 10-digit mobile number";
    }

    // Alt phone validation (if provided)
    if (
      addressData.altphone?.trim() &&
      !/^\d{10}$/.test(addressData.altphone)
    ) {
      errors.altphone = "Alternate number must be 10 digits";
    }

    // Email validation (if provided)
    if (
      addressData.email?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addressData.email)
    ) {
      errors.email = "Please enter a valid email address";
    }

    // Flat/House validation
    if (!addressData.flathouse?.trim()) {
      errors.flathouse = "Flat/House number is required";
    }

    // Area validation
    if (!addressData.area?.trim()) {
      errors.area = "Area/Street is required";
    }

    // City validation
    if (!addressData.city?.trim()) {
      errors.city = "City is required";
    }

    // State validation
    if (!addressData.state?.trim()) {
      errors.state = "State is required";
    }

    // Pincode validation
    if (!addressData.pincode?.trim()) {
      errors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(addressData.pincode)) {
      errors.pincode = "Please enter a valid 6-digit pincode";
    }

    // Address type validation
    if (!addressData.type) {
      errors.type = "Address type is required";
    } else if (!["SHIP", "BILL"].includes(addressData.type)) {
      errors.type = "Address type must be either Shipping or Billing";
    }

    // GST validation (if provided)
    if (addressData.gstno?.trim()) {
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(addressData.gstno)) {
        errors.gstno = "Please enter a valid GST number";
      }
    }

    return errors;
  };
const handleNewAddressChange = (e) => {
  const { name, value, type, checked } = e.target;

  setNewAddress((prev) => ({
    ...prev,
    [name]: name === "gstno" ? value.toUpperCase() : type === "checkbox" ? checked : value,
  }));

  if (formErrors[name]) {
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }

  const upper = value.toUpperCase().trim();
  if (name === "gstno" && gstRegex.test(upper)) {
    fetchGstDetails(upper);
  }
};

const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const fetchGstDetails = async (gstValue) => {
  const gst = gstValue?.trim();
  console.log("Fetching GST details for:", gst);
  if (!gst) return;
  if (!gstRegex.test(gst)) {
    setFormErrors((prev) => ({ ...prev, gstno: "Please enter a valid GST number" }));
    console.warn("Invalid GST number format:", gst);
    return;
  }
  if (lastFetchedGstRef.current === gst) return;

  const requestId = ++gstFetchIdRef.current;
  lastFetchedGstRef.current = gst;
  setGstLoading(true);
  try {
    const res = await getGst(gst);
    if (requestId !== gstFetchIdRef.current) return;

    const gstData = res?.data || res;
    const addressDetails = gstData.address_details || {};

    const gstStateCode = gstData.state_code || gstData.stateCode || "";
    const matchedState = gstStateCode
      ? states.find((s) => String(s.code) === String(gstStateCode))
      : null;

    if (matchedState) {
      setstateCode(String(matchedState.code || matchedState.id || ""));
    }

    setNewAddress((prev) => ({
      ...prev,
      gstno: gst.toUpperCase(),
      name:  prev.name,
      firmname: gstData.legal_name || gstData.trade_name || "",
      flathouse: addressDetails.building_number || prev.flathouse,
      area: addressDetails.street || prev.area,
      landmark: addressDetails.landmark || prev.landmark,
      city: gstData.city || addressDetails.locality || prev.city,
      state: matchedState
        ? (matchedState.statename || matchedState.state || matchedState.name)
        : (gstData.state || prev.state),
      statecode: matchedState ? String(matchedState.code || matchedState.id || "") : prev.statecode,
      pincode: gstData.pincode || addressDetails.pincode || prev.pincode,
    }));
  } catch (err) {
    if (requestId !== gstFetchIdRef.current) return;
    console.error("GST API error:", err);
    showSnackbar("Failed to fetch GST details", "error");
  } finally {
    if (requestId === gstFetchIdRef.current) {
      setGstLoading(false);
    }
  }
};

const handleGstBlur = () => {
  fetchGstDetails(newAddress.gstno);
};
  // Show warning message with all errors
  const showValidationWarning = (errors) => {
    const errorMessages = Object.values(errors).join("\n• ");
    alert(`Please fix the following errors:\n• ${errorMessages}`);
  };

  // ---------------- AUTO DEFAULT ADDRESS ----------------
  // Whenever at least one address exists but none is marked default,
  // automatically set the first (oldest) address as default.
  const ensureDefaultExists = async (addressList) => {
    if (!Array.isArray(addressList) || addressList.length === 0) return;
    if (addressList.some((item) => String(item.default) === "1")) return;

    const oldest = addressList[0];
    try {
      await SetDefaultAddress({
        customerid: userId,
        type: oldest.type,
        id: oldest.id,
      });
      setAddress((prev) =>
        prev.map((item) =>
          item.id === oldest.id ? { ...item, default: "1" } : item,
        ),
      );
    } catch (error) {
      console.error("Failed to set automatic default address:", error);
    }
  };

  // ---------------- FETCH ADDRESS ----------------
  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const res = await getAddress();
      const addressList = Array.isArray(res?.Data) ? res.Data : [];
      setAddress(addressList);
      ensureDefaultExists(addressList);
    } catch (err) {
      console.error("Address API error:", err);
      showSnackbar("Failed to load addresses", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // ---------------- FETCH STATES ----------------
  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const response = await stateList();
      console.log("Full API response:", response);

      const stateData = Array.isArray(response)
        ? response
        : Array.isArray(response?.List)
          ? response.List
          : Array.isArray(response?.Data)
            ? response.Data
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response?.data?.List)
                ? response.data.List
                : Array.isArray(response?.data?.Data)
                  ? response.data.Data
                  : [];

      console.log("Extracted stateData:", stateData);
      setStates(stateData);

      if (stateData.length === 0) {
        console.warn("No states found in the response");
        showSnackbar("No states available", "info");
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      showSnackbar("Failed to load states", "error");
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  // Monitor states changes
  useEffect(() => {
    console.log("Current states in state variable:", states);
  }, [states]);

  const handleSetDefault = async (userId, type, id) => {
    try {
      const response = await SetDefaultAddress({
        customerid: userId,
        type: type,
        id: id,
      });
      console.log("default:", response);
      showSnackbar("Default address updated successfully!");
      fetchAddresses();
    } catch (error) {
      console.error("Failed to set default address", error);
      showSnackbar("Failed to set default address", "error");
    }
  };

  // ---------------- GET USER DATA ----------------
  const getUserData = () => {
    return {
      name:
        localStorage.getItem("firstName") ||
        localStorage.getItem("first_name") ||
        localStorage.getItem("name") ||
        "",
      email:
        localStorage.getItem("email") ||
        localStorage.getItem("userName") ||
        "",
      phone:
        localStorage.getItem("mobile") ||
        localStorage.getItem("Mobile") ||
        "",
      userId: localStorage.getItem("userId") || "",
      gst: localStorage.getItem("GST") || "",
      usertype:
        localStorage.getItem("userType") ||
        localStorage.getItem("usertype") ||
        "",
    };
  };

  // ---------------- OPEN ADD MODAL ----------------
  const handleOpenAddModal = () => {
    const userData = getUserData();
    console.log(userData.name)
    setNewAddress({
      name: userData.name,
      firmname: userData.gst ? userData.gst : "",
      phone: userData.phone,
      altphone: "",
      email: userData.email,
      flathouse: "",
      area: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      statecode: statecode,
      stateid: Number(statecode),
      type: "",
      gstno: userData.gst,
      useSameAddressForBilling: true,
    });

    setFormErrors({});
    setShowAddModal(true);
  };

  // ---------------- HANDLE ADD ADDRESS ----------------
  const handleAddAddress = async () => {
    const userData = getUserData();

    // Validate form
    const errors = validateAddressForm(newAddress);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showValidationWarning(errors);
      return;
    }

    setAdding(true);
    console.log("statecode", statecode)
    try {
      const response = await AddAddress({
        addressType: newAddress.type,
        altphone: newAddress.altphone,
        area: newAddress.area,
        city: newAddress.city,
        country: newAddress.country,
        customerid: userData.userId,
        email: newAddress.email,
        flathouse: newAddress.flathouse,
        gstno: newAddress.gstno,
        landmark: newAddress.landmark,
        name: newAddress.name,
        firmname: newAddress.firmname,
        phone: newAddress.phone,
        pincode: newAddress.pincode,
        state: newAddress.state,
        statecode: statecode,
        stateid: Number(statecode),
        type: newAddress.type,
        useSameAddressForBilling: newAddress.useSameAddressForBilling,
      });

      console.log("Add address response:", response);

      if (
        response &&
        (response.status === 200 ||
          response.status === 201 ||
          response.success === true)
      ) {
        const refreshed = await getAddress();
        const updatedAddresses = refreshed?.Data || [];
        const targetTypes = newAddress.useSameAddressForBilling
          ? ["SHIP", "BILL"]
          : [newAddress.type];

        const defaultIdsByType = {};

        targetTypes.forEach((type) => {
          const sameTypeMatches = updatedAddresses.filter((item) => item.type === type);
          const exactMatch = sameTypeMatches
            .filter((item) => {
              const matchesAddress =
                String(item.phone || "") === String(newAddress.phone || "") &&
                String(item.flathouse || "") === String(newAddress.flathouse || "") &&
                String(item.area || "") === String(newAddress.area || "") &&
                String(item.city || "") === String(newAddress.city || "") &&
                String(item.pincode || "") === String(newAddress.pincode || "");

              return matchesAddress;
            })
            .sort((a, b) => Number(b.id) - Number(a.id));

          const primaryDefaultId = exactMatch[0]?.id || sameTypeMatches.sort((a, b) => Number(b.id) - Number(a.id))[0]?.id;
          if (primaryDefaultId) {
            defaultIdsByType[type] = primaryDefaultId;
          }
        });

        if (Object.keys(defaultIdsByType).length > 0) {
          await Promise.all(
            Object.entries(defaultIdsByType).map(([type, id]) =>
              SetDefaultAddress({
                customerid: userData.userId,
                type,
                id,
              }),
            ),
          );

          const defaultIds = new Set(Object.values(defaultIdsByType));
          setAddress(
            updatedAddresses.map((item) => {
              const isThisTypeDefault = item.type in defaultIdsByType &&
                defaultIdsByType[item.type] === item.id;

              return {
                ...item,
                default: isThisTypeDefault ? "1" : item.type in defaultIdsByType ? "0" : item.default,
              };
            }),
          );
        } else {
          setAddress(updatedAddresses);
        }

        setShowAddModal(false);
        setNewAddress({
          name: "",
          firmname: "",
          phone: "",
          altphone: "",
          email: "",
          flathouse: "",
          area: "",
          landmark: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
          type: "",
          statecode: '',
          stateid: 0,
          gstno: "",
          useSameAddressForBilling: true,
        });
        setFormErrors({});
        showSnackbar("Address added successfully!");
      } else {
        showSnackbar("Failed to add address", "error");
      }
    } catch (err) {
      console.error("Add address failed:", err);
      showSnackbar("Failed to add address", "error");
    } finally {
      setAdding(false);
    }
  };

  // ---------------- OPEN EDIT ----------------
  const handleEditClick = (item, e) => {
    e.preventDefault();
    e.stopPropagation();

    const editItem = {
      id: item.id,
      name: item.name || "",
      phone: item.phone || "",
      altphone: item.altphone || "",
      email: item.email || "",
      flathouse: item.flathouse || "",
      area: item.area || "",
      landmark: item.landmark || "",
      city: item.city || "",
      state: item.state || "",
      statecode: statecode,
      stateid: Number(statecode),
      pincode: item.pincode || "",
      country: item.country || "India",
      type: item.type || "",
      gstno: item.gstno || "",
      customerid: userId,
      useSameAddressForBilling: item.useSameAddressForBilling || true,
    };

    setEditData(editItem);
    setFormErrors({});
    setShowEdit(true);
  };

  // ---------------- HANDLE EDIT INPUT CHANGE ----------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // ---------------- INPUT CHANGE FOR NEW ADDRESS ----------------
  // const handleNewAddressChange = (e) => {
  //   const { name, value, type, checked } = e.target;
  //   console.log(name, value, type, checked)
  //   // setStatecode()
  //   setNewAddress((prev) => ({
  //     ...prev,
  //     [name]: type === "checkbox" ? checked : value,
  //   }));

  //   // Clear error for this field when user starts typing
  //   if (formErrors[name]) {
  //     setFormErrors((prev) => {
  //       const newErrors = { ...prev };
  //       delete newErrors[name];
  //       return newErrors;
  //     });
  //   }
  // };

  // ---------------- SAVE EDIT ----------------
  const handleSaveEdit = async () => {
    if (!editData || !userId) {
      showSnackbar("Missing data or user ID", "error");
      return;
    }

    // Validate form
    const errors = validateAddressForm(editData);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showValidationWarning(errors);
      return;
    }

    setSaving(true);
    try {
      console.log("Saving edit data:", editData);

      const response = await EditAddress({
        ...editData,
        customerid: userId,
      });

      console.log("Edit response:", response);

      if (
        response &&
        (response.status === 200 ||
          response.status === 201 ||
          response.success === true)
      ) {
        setShowEdit(false);
        setEditData(null);
        setFormErrors({});
        fetchAddresses();
        showSnackbar("Address updated successfully!");
      } else {
        showSnackbar("Failed to update address", "error");
      }
    } catch (err) {
      console.error("Save failed:", err);
      showSnackbar("Failed to update address", "error");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- CLOSE MODALS ----------------
  const handleCloseModal = () => {
    setShowEdit(false);
    setEditData(null);
    setFormErrors({});
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewAddress({
      name: "",
      phone: "",
      altphone: "",
      email: "",
      flathouse: "",
      area: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      type: "",
      statecode: '',
      stateid: 0,
      gstno: "",
      useSameAddressForBilling: true,
    });
    setFormErrors({});
  };

  // ---------------- HANDLE DELETE ----------------
  const handleDelete = async (id) => {
    try {
      await DeleteAddress({ id });
      showSnackbar("Address deleted successfully!");
      fetchAddresses();
    } catch (error) {
      console.error(error);
      showSnackbar("Failed to delete address", "error");
    }
  };

  // Function to get display type for address card
  const getDisplayType = (type) => {
    if (type === "BILL") return "Billing";
    if (type === "SHIP") return "Shipping";
    return "Other";
  };

  // State Dropdown Component
  const StateDropdown = ({ value, onChange, error }) => (
    <div className={`form-group ${error ? "has-error" : ""}`}>
      <label>
        State <span className="required-star">*</span>
      </label>
      <select
        name="state"
        value={value || ""}
        onChange={(e) => {
          const selectedName = e.target.value;

          onChange(e);

          const selectedState = states.find((s) => s.statename === selectedName);
          setstateCode(selectedState?.code || "");
        }}
        className={error ? "error" : ""}
        disabled={loadingStates}
      >
        <option value="">
          {loadingStates ? "Loading states..." : "Select State"}
        </option>
        {states.length > 0 ? (
          states.map((stateItem, index) => {
            const stateName =
              stateItem.statename || stateItem.state || stateItem.name;
            const stateValue = stateItem.code || stateItem.id || index;

            return (
              <option key={stateValue} value={stateName}>
                {stateName}
              </option>
            );
          })
        ) : (
          <option value="" disabled>
            {loadingStates ? "Loading..." : "No states available"}
          </option>
        )}
      </select>
      {error && <span className="error-message">{error}</span>}
      {!loadingStates && states.length === 0 && (
        <span className="info-message">Debug: States array is empty</span>
      )}
    </div>
  );

  return (
    <div className="address-page">
      <h2 className="page-title">My Addresses</h2>


      {loading ? (
        <LoadingSpinner text="Loading addresses..." />
      ) : address.length === 0 ? (
        <div className="no-addresses">
          <p>No addresses found. Add your first address!</p>
          <button className="btn-primary" onClick={handleOpenAddModal}>
            + Add New Address
          </button>
        </div>
      ) : (
        <>
          <div className="address-grid">
            {address.map((item) => (
              <div
                key={item.id}
                className={`address-card ${selectedAddress === item.id ? "active" : ""}`}
                onClick={() => setSelectedAddress(item.id)}
              >
                <div className="address-header">
                  <span className="address-type">
                    {getDisplayType(item.type)}
                  </span>
                  {item.default === "1" ? (
                    <span className="default-badge">DEFAULT</span>
                  ) : (
                    <button
                      className="btn-outline set-default-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(userId, item.type, item.id);
                      }}
                    >
                      Set as Default
                    </button>
                  )}
                </div>

                <p className="name">{item.name}</p>
                <p className="text">
                  {item.flathouse}, {item.area}
                </p>
                <p className="text">
                  {item.city}, {item.state} - {item.pincode}
                </p>
                <p className="phone">{item.phone}</p>
                {item.gstno && (
                  <p className="gst-number">
                    <strong>GST:</strong> {item.gstno}
                  </p>
                )}

                <div className="address-actions">
                  <button
                    className="btn-outline"
                    onClick={(e) => handleEditClick(item, e)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn-outline danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="add-address-section">
            <button className="btn-primary" onClick={handleOpenAddModal}>
              + Add New Address
            </button>
          </div>
        </>
      )}

      {/* ADD ADDRESS MODAL */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseAddModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Address</h3>
              <button className="close-btn" onClick={handleCloseAddModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Name */}
              <div className={`form-group ${formErrors.name ? "has-error" : ""}`}>
                  <div className={`form-group ${formErrors.gstno ? "has-error" : ""}`}>
                <label>GST Number</label>
                <input
                  type="text"
                  name="gstno"
                  value={newAddress.gstno}
                  onChange={handleNewAddressChange}
                  onBlur={handleGstBlur}
                  placeholder="GST number (if applicable)"
                  className={formErrors.gstno ? "error" : ""}
                />
                {gstLoading && <span className="info-message">Fetching GST details...</span>}
                {formErrors.gstno && <span className="error-message">{formErrors.gstno}</span>}
              </div>
                <label>Full Name <span className="required-star">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={newAddress.name}
                  onChange={handleNewAddressChange}
                  placeholder="Enter full name"
                  className={formErrors.name ? "error" : ""}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              {/* Firm Name */}
              <div className={`form-group ${formErrors.firmname ? "has-error" : ""}`}>
                <label>Firm Name</label>
                <input
                  type="text"
                  name="firmname"
                  value={newAddress.firmname}
                  onChange={handleNewAddressChange}
                  placeholder="Enter firm name"
                  className={formErrors.firmname ? "error" : ""}
                />
                {formErrors.firmname && <span className="error-message">{formErrors.firmname}</span>}
              </div>

              {/* Phone and Alt Phone */}
              <div className="form-row">
                <div className={`form-group ${formErrors.phone ? "has-error" : ""}`}>
                  <label>Mobile Number <span className="required-star">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={newAddress.phone}
                    onChange={handleNewAddressChange}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                    className={formErrors.phone ? "error" : ""}
                  />
                  {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                </div>

                <div className={`form-group ${formErrors.altphone ? "has-error" : ""}`}>
                  <label>Alternate Number</label>
                  <input
                    type="tel"
                    name="altphone"
                    value={newAddress.altphone}
                    onChange={handleNewAddressChange}
                    placeholder="Alternate number"
                    maxLength="10"
                    className={formErrors.altphone ? "error" : ""}
                  />
                  {formErrors.altphone && <span className="error-message">{formErrors.altphone}</span>}
                </div>
              </div>

              {/* Email */}
              <div className={`form-group ${formErrors.email ? "has-error" : ""}`}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={newAddress.email}
                  onChange={handleNewAddressChange}
                  placeholder="Enter email"
                  className={formErrors.email ? "error" : ""}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>

              {/* Flat/House */}
              <div className={`form-group ${formErrors.flathouse ? "has-error" : ""}`}>
                <label>Flat/House No. <span className="required-star">*</span></label>
                <input
                  type="text"
                  name="flathouse"
                  value={newAddress.flathouse}
                  onChange={handleNewAddressChange}
                  placeholder="House number, building name"
                  className={formErrors.flathouse ? "error" : ""}
                />
                {formErrors.flathouse && <span className="error-message">{formErrors.flathouse}</span>}
              </div>

              {/* Area/Street */}
              <div className={`form-group ${formErrors.area ? "has-error" : ""}`}>
                <label>Area/Street <span className="required-star">*</span></label>
                <input
                  type="text"
                  name="area"
                  value={newAddress.area}
                  onChange={handleNewAddressChange}
                  placeholder="Area, street, sector, village"
                  className={formErrors.area ? "error" : ""}
                />
                {formErrors.area && <span className="error-message">{formErrors.area}</span>}
              </div>

              {/* Landmark */}
              <div className={`form-group ${formErrors.landmark ? "has-error" : ""}`}>
                <label>Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  value={newAddress.landmark}
                  onChange={handleNewAddressChange}
                  placeholder="Nearby landmark"
                  className={formErrors.landmark ? "error" : ""}
                />
                {formErrors.landmark && <span className="error-message">{formErrors.landmark}</span>}
              </div>

              {/* City and State */}
              <div className="form-row">
                <div className={`form-group ${formErrors.city ? "has-error" : ""}`}>
                  <label>City <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="city"
                    value={newAddress.city}
                    onChange={handleNewAddressChange}
                    placeholder="City"
                    className={formErrors.city ? "error" : ""}
                  />
                  {formErrors.city && <span className="error-message">{formErrors.city}</span>}
                </div>

                {/* State Dropdown */}
                <StateDropdown
                  value={newAddress.state}
                  onChange={handleNewAddressChange}
                  error={formErrors.state}
                />
              </div>

              {/* Pincode and Address Type */}
              <div className="form-row">
                <div className={`form-group ${formErrors.pincode ? "has-error" : ""}`}>
                  <label>Pincode <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="pincode"
                    value={newAddress.pincode}
                    onChange={handleNewAddressChange}
                    placeholder="6-digit pincode"
                    maxLength="6"
                    className={formErrors.pincode ? "error" : ""}
                  />
                  {formErrors.pincode && <span className="error-message">{formErrors.pincode}</span>}
                </div>

                <div className={`form-group ${formErrors.type ? "has-error" : ""}`}>
                  <label>Address Type <span className="required-star">*</span></label>
                  <select
                    name="type"
                    value={newAddress.type}
                    onChange={handleNewAddressChange}
                    className={formErrors.type ? "error" : ""}
                  >
                    <option value="">Select Address Type</option>
                    <option value="BILL">Billing</option>
                    <option value="SHIP">Shipping</option>
                  </select>
                  {formErrors.type && <span className="error-message">{formErrors.type}</span>}
                  <span style={{ display: "flex", gap: "6px", marginLeft: '9px' }}>
                    Use this for both
                    <span style={{ color: "black" }}>*</span>
                    <input
                      type="checkbox"
                      id="useSameAddressForBilling-add"
                      name="useSameAddressForBilling"
                      checked={newAddress.useSameAddressForBilling}
                      onChange={handleNewAddressChange}
                      style={{
                        width: 'auto',
                        margin: 0,
                      }}
                    />
                  </span>

                </div>
              </div>

             

              {/* Use Same Address for Billing */}


              {/* Country (Disabled) */}
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={newAddress.country}
                  onChange={handleNewAddressChange}
                  disabled
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={handleCloseAddModal}
                disabled={adding}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleAddAddress}
                disabled={adding}
              >
                {adding ? "Adding..." : "Add Address"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT ADDRESS MODAL */}
      {showEdit && editData && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Address</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Name */}
              <div className={`form-group ${formErrors.name ? "has-error" : ""}`}>
                <label>Full Name <span className="required-star">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={editData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className={formErrors.name ? "error" : ""}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              {/* Phone and Alt Phone */}
              <div className="form-row">
                <div className={`form-group ${formErrors.phone ? "has-error" : ""}`}>
                  <label>Mobile Number <span className="required-star">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={editData.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    className={formErrors.phone ? "error" : ""}
                  />
                  {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                </div>

                <div className={`form-group ${formErrors.altphone ? "has-error" : ""}`}>
                  <label>Alternate Number</label>
                  <input
                    type="tel"
                    name="altphone"
                    value={editData.altphone}
                    onChange={handleChange}
                    placeholder="Alternate number"
                    className={formErrors.altphone ? "error" : ""}
                  />
                  {formErrors.altphone && <span className="error-message">{formErrors.altphone}</span>}
                </div>
              </div>

              {/* Email */}
              <div className={`form-group ${formErrors.email ? "has-error" : ""}`}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  className={formErrors.email ? "error" : ""}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>

              {/* Flat/House */}
              <div className={`form-group ${formErrors.flathouse ? "has-error" : ""}`}>
                <label>Flat/House No. <span className="required-star">*</span></label>
                <input
                  type="text"
                  name="flathouse"
                  value={editData.flathouse}
                  onChange={handleChange}
                  placeholder="House number, building name"
                  className={formErrors.flathouse ? "error" : ""}
                />
                {formErrors.flathouse && <span className="error-message">{formErrors.flathouse}</span>}
              </div>

              {/* Area/Street */}
              <div className={`form-group ${formErrors.area ? "has-error" : ""}`}>
                <label>Area/Street <span className="required-star">*</span></label>
                <input
                  type="text"
                  name="area"
                  value={editData.area}
                  onChange={handleChange}
                  placeholder="Area, street, sector, village"
                  className={formErrors.area ? "error" : ""}
                />
                {formErrors.area && <span className="error-message">{formErrors.area}</span>}
              </div>

              {/* Landmark */}
              <div className={`form-group ${formErrors.landmark ? "has-error" : ""}`}>
                <label>Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  value={editData.landmark}
                  onChange={handleChange}
                  placeholder="Nearby landmark"
                  className={formErrors.landmark ? "error" : ""}
                />
                {formErrors.landmark && <span className="error-message">{formErrors.landmark}</span>}
              </div>

              {/* City and State */}
              <div className="form-row">
                <div className={`form-group ${formErrors.city ? "has-error" : ""}`}>
                  <label>City <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="city"
                    value={editData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className={formErrors.city ? "error" : ""}
                  />
                  {formErrors.city && <span className="error-message">{formErrors.city}</span>}
                </div>

                {/* State Dropdown */}
                <StateDropdown
                  value={editData.state}
                  onChange={handleChange}
                  error={formErrors.state}
                />
              </div>

              {/* Pincode and Address Type */}
              <div className="form-row">
                <div className={`form-group ${formErrors.pincode ? "has-error" : ""}`}>
                  <label>Pincode <span className="required-star">*</span></label>
                  <input
                    type="text"
                    name="pincode"
                    value={editData.pincode}
                    onChange={handleChange}
                    placeholder="6-digit pincode"
                    className={formErrors.pincode ? "error" : ""}
                  />
                  {formErrors.pincode && <span className="error-message">{formErrors.pincode}</span>}
                </div>

                <div className={`form-group ${formErrors.type ? "has-error" : ""}`}>
                  <label>Address Type <span className="required-star">*</span></label>
                  <select
                    name="type"
                    value={editData.type || ""}
                    onChange={handleChange}
                    className={formErrors.type ? "error" : ""}
                  >
                    <option value="">Select Address Type</option>
                    <option value="BILL">Billing</option>
                    <option value="SHIP">Shipping</option>
                  </select>
                  {formErrors.type && <span className="error-message">{formErrors.type}</span>}
                </div>
              </div>

              {/* GST Number */}
              <div className={`form-group ${formErrors.gstno ? "has-error" : ""}`}>
                <label>GST Number</label>
                <input
                  type="text"
                  name="gstno"
                  value={editData.gstno || ""}
                  onChange={handleChange}
                  placeholder="GST number (if applicable)"
                  className={formErrors.gstno ? "error" : ""}
                />
                {formErrors.gstno && <span className="error-message">{formErrors.gstno}</span>}
              </div>

              {/* Use Same Address for Billing */}
              <div className="form-group">
                <label
                  htmlFor="useSameAddressForBilling-add"
                  style={{
                    display: "inline-flex", // 👈 NOT flex (prevents full-width stretch)
                    alignItems: "center",
                    gap: "6px", // 👈 control spacing here
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>
                    Use this address for billing{" "}
                    <span style={{ color: "black" }}>*</span>
                  </span>
                  <input
                    type="checkbox"
                    id="useSameAddressForBilling-add"
                    name="useSameAddressForBilling"
                    checked={newAddress.useSameAddressForBilling}
                    onChange={handleNewAddressChange}
                    style={{
                      margin: 0,
                    }}
                  />


                </label>
              </div>

              {/* Country (Disabled) */}
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={editData.country}
                  onChange={handleChange}
                  disabled
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={handleCloseModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Address;
